import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TabsContent } from '@/components/ui/tabs';
import { formatCurrency, formatPercent } from '@/lib/utils/formatters';
import { useGestaoImpostosContext } from '@/contexts';
import type { ValorProjetado } from '@/lib/tax/cronogramaTransicaoLC214';
import { AvisoCalculoDemorado } from '@/components/AvisoCalculoDemorado';

const LABELS: Record<'icms' | 'ipi' | 'ibs' | 'cbs', string> = {
  icms: 'ICMS',
  ipi: 'IPI',
  ibs: 'IBS',
  cbs: 'CBS',
};

function LinhaImposto({ label, dados }: { label: string; dados: ValorProjetado }) {
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex-1">
        <div className="font-medium text-lg uppercase">{label}</div>
        <div className="text-sm text-muted-foreground">
          {formatPercent(dados.percentualSobreVendas)} sobre vendas
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold text-red-600">{formatCurrency(dados.valor)}</div>
      </div>
    </div>
  );
}

export function SituacaoAtualTab() {
  const { isLoadingSituacaoAtual, projecaoAnoAtual, aliquotaIS, setAliquotaIS } =
    useGestaoImpostosContext();

  return (
    <TabsContent value="atual" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento de Impostos - Situação Atual</CardTitle>
          <CardDescription>Breakdown completo da carga tributária sobre as vendas</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSituacaoAtual && <AvisoCalculoDemorado />}
          {isLoadingSituacaoAtual || !projecaoAnoAtual ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <LinhaImposto label={LABELS.icms} dados={projecaoAnoAtual.icms} />
              <LinhaImposto label={LABELS.ipi} dados={projecaoAnoAtual.ipi} />
              <LinhaImposto label={LABELS.ibs} dados={projecaoAnoAtual.ibs} />
              <LinhaImposto label={LABELS.cbs} dados={projecaoAnoAtual.cbs} />

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-lg uppercase">IS</span>
                    <Badge variant="secondary" className="gap-1.5">
                      Alíquota
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={aliquotaIS}
                        onChange={e => setAliquotaIS(Number(e.target.value) || 0)}
                        className="h-6 w-16 border-0 bg-transparent p-0 text-right"
                        aria-label="Alíquota do Imposto Seletivo (%)"
                      />
                      %
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Ainda não definida por lei — informe manualmente
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-red-600">
                    {formatCurrency(projecaoAnoAtual.is.valor)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border-2 border-primary rounded-lg bg-primary/5">
                <div className="flex-1">
                  <div className="font-bold text-xl">TOTAL</div>
                  <div className="text-sm text-muted-foreground">
                    {formatPercent(projecaoAnoAtual.total.percentualSobreVendas)} sobre vendas
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">
                    {formatCurrency(projecaoAnoAtual.total.valor)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
